// An order line's `quantity` is what the client ordered; it is NOT what the tote
// holds. A picker who finds pieces damaged unlinks them from the line
// (`order_line_id: nil`) and leaves the quantity untouched — the order ships short
// by design, resolved outside the picking flow. Counting scans against the line
// would then demand pieces that no longer exist anywhere (BUGS-8257).
//
// The stock units still attached to the order are the physical truth. Every state
// that matters stays attached — PICKED in the tote, PACKED in a caja, SHIPPED once
// concluded — so they are counted without filtering by status. Cap each product's
// required count to them, never raise it.
//
// Three states, not two, because the cap is dangerous in BOTH directions. It feeds
// `validQualityCheck`, which compares Σ quantity against Σ reviewedItemsCount:
//   - too high and the operator can never complete the count, with no exit from
//     inside the screen;
//   - all the way to zero and the sum reads 0 === 0, the gate says "complete", and
//     the footer enables with NOTHING packed — the exact short ship this branch
//     exists to remove.
// When those two risks collide we refuse (state 2 below): an unreachable count is a
// bad day, a count that reads complete over an empty caja is a short shipment.
export const capRequiredToAssignedUnits = (productsById, orderLines = []) => {
  const lines = orderLines || []

  // Did EVERY line report its units? This screen reads orders#show_wms ->
  // OrderLineWmsSerializer, whose `has_many :stock_units` carries no `unless:`, so in
  // practice the key is always there and this guard is defensive rather than a live
  // path. (The conditional one is OrderLineSerializer's, used by
  // OrderValidationSerializer, not here — an earlier revision of this comment cited
  // that one and was wrong about which payload this function receives.)
  //
  // `every`, not `some`: a line that never reported its units contributes nothing to
  // the map, so trusting a partial payload would cap the silent lines to zero on the
  // strength of the ones that did answer. Absent data must leave the declared count
  // alone, and requiring all of them to answer is the reading that fails closed.
  const carriedStockUnits = lines.length > 0 && lines.every(orderLine => Array.isArray(orderLine?.stock_units))

  const assignedByProductId = {}
  // Counted separately from the map on purpose. `linkedUnits` asks "does the order
  // still have pieces attached", which is what the floor below turns on, so a unit
  // counts here even when it carries no usable `product_id` — such a unit is still a
  // piece the order holds, and skipping it would let the floor disengage and cap the
  // whole map to zero. (`stock_units.product_id` is NOT NULL and StockUnitWmsSerializer
  // always emits it, so this is a guard against a shape the API should never send, not
  // a live case.) The map itself can only key on a product id it actually has.
  let linkedUnits = 0
  lines.forEach(orderLine => {
    ;(orderLine?.stock_units || []).forEach(stockUnit => {
      if (!stockUnit) return
      linkedUnits += 1
      if (!stockUnit.product_id) return
      assignedByProductId[stockUnit.product_id] = (assignedByProductId[stockUnit.product_id] || 0) + 1
    })
  })

  // STATE 1 — the payload did not fully report its units. We have incomplete
  // information, so the declared quantity stands. Capping on absent data would let
  // anyone emit a label with nothing scanned.
  if (!carriedStockUnits) return productsById

  const products = Object.values(productsById || {})
  // What the cap below would leave, summed over the order. The cap only ever lowers,
  // so each product's post-cap count is min(declared, assigned).
  const cappedTotal = products.reduce((total, product) => total + Math.min(product?.quantity || 0, assignedByProductId[product?.id] ?? 0), 0)

  // STATE 2 — the cap would zero the WHOLE order while the order still has units
  // linked to it. That is not "the pieces are gone", it is "the units are attributed
  // to product ids this map does not know": a kit re-composed after reservation
  // leaves the map keyed by the CURRENT child ids while the units still carry the
  // previous ones, so `assigned` is 0 for every child. Fail closed and leave the
  // declared counts — a zeroed map satisfies `validQualityCheck` vacuously and
  // enables the label over an empty caja, and on a D2C order the server gate is
  // unarmed in that same state, so nothing downstream would stop it.
  //
  // Order-level on purpose: the failure mode is "the whole map went to zero", and a
  // per-product floor of 1 would invent a required piece on a legitimately short line.
  if (linkedUnits > 0 && cappedTotal === 0) return productsById

  // STATE 3 — cap, never raise. Past here a product missing from the map really has
  // none left: either other products account for the order's linked units, or the
  // payload said explicitly that nothing is linked (`stock_units: []` on every line).
  products.forEach(product => {
    const assigned = assignedByProductId[product.id] ?? 0
    if (assigned < product.quantity) product.quantity = assigned
  })
  return productsById
}
